import { Command } from "commander"
import pc from "picocolors"
import {
  type Account,
  type Address,
  type Chain,
  createPublicClient,
  http,
  isAddress,
  type Transport,
  type WalletClient,
} from "viem"
import { validateManifest } from "../../lib/manifest/index.js"
import { IAccessPredicateABI } from "../../lib/onchain/abis.js"
import {
  deploymentAddress,
  ERC20_BALANCE_PREDICATE,
  ERC721_OWNER_PREDICATE,
  SUBSCRIPTION_PREDICATE,
  TOOL_REGISTRY,
} from "../../lib/onchain/chains.js"
import { computeManifestHash } from "../../lib/onchain/hash.js"
import {
  ERC20BalancePredicateClient,
  ERC721OwnerPredicateClient,
  ERC1155OwnerPredicateClient,
  SubscriptionPredicateClient,
} from "../../lib/onchain/predicate-clients.js"
import { ToolRegistryClient } from "../../lib/onchain/registry.js"
import {
  createWalletForProvider,
  createWalletFromEnv,
  type WalletProvider,
  walletAdapterToClient,
} from "../../lib/wallet/index.js"
import { getChain, NETWORK_OPTION_DESCRIPTION } from "./get-chain.js"
import {
  RPC_URL_OPTION_DESCRIPTION,
  resolveRpcUrl,
  WALLET_PROVIDER_OPTION_DESCRIPTION,
} from "./shared.js"
import { warnBareExtensionKeys } from "./warn-bare-extensions.js"

interface RegisterOptions {
  metadata: string
  network: string
  nftGate?: string
  erc20Gate?: string
  erc20MinBalance?: string
  accessPredicate?: string
  predicateConfig?: string
  walletProvider?: string
  rpcUrl?: string
  dryRun?: boolean
  yes?: boolean
}

export const registerCommand = new Command("register")
  .description("Register a tool onchain via the ToolRegistry")
  .option("--metadata <url>", "Metadata URI (required)")
  .option("--network <network>", NETWORK_OPTION_DESCRIPTION, "base")
  .option(
    "--nft-gate <address>",
    "ERC-721 collection address; gates the tool via the canonical ERC721OwnerPredicate (version auto-detected from registry)",
  )
  .option(
    "--erc20-gate <token>",
    "ERC-20 token address; gates the tool via the canonical ERC20BalancePredicate and configures it",
  )
  .option(
    "--erc20-min-balance <amount>",
    "Minimum token balance required (raw units, e.g. 1000000000000000000 for 1e18); required with --erc20-gate",
  )
  .option("--access-predicate <address>", "Access predicate address")
  .option(
    "--predicate-config <json>",
    'JSON config for the access predicate (e.g. \'{"collections":["0x..."]}\')',
  )
  .option("--wallet-provider <provider>", WALLET_PROVIDER_OPTION_DESCRIPTION)
  .option("--rpc-url <url>", RPC_URL_OPTION_DESCRIPTION)
  .option("--dry-run", "Print summary without transacting")
  .option("-y, --yes", "Skip confirmation prompt")
  .action(async (options: RegisterOptions) => {
    if (!options.metadata) {
      console.error(pc.red("Error: --metadata is required"))
      process.exit(1)
    }

    const gateFlags = [
      options.nftGate && "--nft-gate",
      options.erc20Gate && "--erc20-gate",
      options.accessPredicate && "--access-predicate",
    ].filter(Boolean) as string[]
    if (gateFlags.length > 1) {
      console.error(
        pc.red(`Error: ${gateFlags.join(" and ")} are mutually exclusive`),
      )
      process.exit(1)
    }

    if (options.nftGate && !isAddress(options.nftGate)) {
      console.error(
        pc.red(
          `Error: --nft-gate value "${options.nftGate}" is not a valid address`,
        ),
      )
      process.exit(1)
    }

    if (options.erc20Gate && !isAddress(options.erc20Gate)) {
      console.error(
        pc.red(
          `Error: --erc20-gate value "${options.erc20Gate}" is not a valid address`,
        ),
      )
      process.exit(1)
    }

    if (options.erc20Gate && !options.erc20MinBalance) {
      console.error(
        pc.red("Error: --erc20-min-balance is required with --erc20-gate"),
      )
      process.exit(1)
    }

    if (options.erc20MinBalance && !options.erc20Gate) {
      console.error(pc.red("Error: --erc20-min-balance requires --erc20-gate"))
      process.exit(1)
    }

    let erc20MinBalance: bigint | undefined
    if (options.erc20MinBalance) {
      try {
        erc20MinBalance = BigInt(options.erc20MinBalance)
        if (erc20MinBalance <= 0n) throw new Error("must be positive")
      } catch {
        console.error(
          pc.red("Error: --erc20-min-balance must be a positive integer"),
        )
        process.exit(1)
        return
      }
    }

    if (options.predicateConfig && !options.accessPredicate) {
      console.error(
        pc.red("Error: --predicate-config requires --access-predicate"),
      )
      process.exit(1)
    }

    let predicateConfig: Record<string, unknown> | undefined
    if (options.predicateConfig) {
      try {
        predicateConfig = JSON.parse(options.predicateConfig) as Record<
          string,
          unknown
        >
      } catch {
        console.error(pc.red("Error: --predicate-config is not valid JSON"))
        process.exit(1)
        return
      }
    }

    if (options.accessPredicate && !isAddress(options.accessPredicate)) {
      console.error(
        pc.red(
          `Error: --access-predicate value "${options.accessPredicate}" is not a valid address`,
        ),
      )
      process.exit(1)
    }

    console.log(pc.cyan("Verifying manifest..."))

    let response: globalThis.Response
    try {
      response = await fetch(options.metadata, {
        redirect: "manual",
      })
    } catch {
      console.error(pc.red(`Error: Failed to fetch ${options.metadata}`))
      process.exit(1)
    }

    if (response.status !== 200) {
      console.error(pc.red(`Error: HTTP ${response.status}`))
      process.exit(1)
    }

    let data: unknown
    try {
      data = await response.json()
    } catch {
      console.error(pc.red("Error: Response is not valid JSON"))
      process.exit(1)
    }

    const result = validateManifest(data)
    if (!result.success) {
      console.error(pc.red("Error: Manifest validation failed"))
      process.exit(1)
    }

    warnBareExtensionKeys(data)

    const manifest = result.data

    const manifestOrigin = new URL(options.metadata).origin
    const endpointOrigin = new URL(manifest.endpoint).origin
    if (manifestOrigin !== endpointOrigin) {
      console.error(
        pc.red(
          "Error: Origin mismatch between manifest URL and declared endpoint",
        ),
      )
      console.error(pc.dim(`  Manifest origin:  ${manifestOrigin}`))
      console.error(pc.dim(`  Endpoint origin:  ${endpointOrigin}`))
      console.error()
      console.error(
        pc.yellow(
          "  The manifest and endpoint must be served from the exact same origin (scheme + host + port).",
        ),
      )
      console.error(
        pc.yellow(
          "  Subdomains count as different origins (e.g. example.com ≠ api.example.com).",
        ),
      )
      console.error(
        pc.yellow(
          "  Move your manifest or endpoint so both share the same host, then retry.",
        ),
      )
      process.exit(1)
    }

    // Hash the manifest as served (ERC-8257 §2): the full fetched document,
    // not the schema-stripped copy.
    const hash = computeManifestHash(data as object)
    const chain = getChain(options.network)

    const registryAddr = deploymentAddress(TOOL_REGISTRY, chain.id)
    if (!registryAddr) {
      console.error(
        pc.red(
          `Error: ToolRegistry is not deployed on ${options.network}. See https://github.com/ProjectOpenSea/tool-registry#readme for supported chains.`,
        ),
      )
      process.exit(1)
    }

    let accessPredicate =
      "0x0000000000000000000000000000000000000000" as `0x${string}`
    let nftGateInfo: { predicateAddr: `0x${string}` } | undefined
    let erc20GateInfo:
      | {
          predicateAddr: `0x${string}`
          token: `0x${string}`
          minBalance: bigint
        }
      | undefined

    let predicateName: string | undefined

    const wallet = options.walletProvider
      ? createWalletForProvider(options.walletProvider as WalletProvider)
      : createWalletFromEnv()
    const rpcUrl = resolveRpcUrl(options.rpcUrl, wallet, chain)

    if (options.accessPredicate) {
      accessPredicate = options.accessPredicate as `0x${string}`

      const publicClient = createPublicClient({
        chain,
        transport: http(rpcUrl),
      })
      try {
        predicateName = await publicClient.readContract({
          address: accessPredicate,
          abi: IAccessPredicateABI,
          functionName: "name",
        })
      } catch {
        predicateName = undefined
      }

      if (predicateConfig && predicateName) {
        validatePredicateConfig(predicateName, predicateConfig)
      }
    } else if (options.nftGate) {
      const predicateAddr = deploymentAddress(ERC721_OWNER_PREDICATE, chain.id)
      if (!predicateAddr) {
        console.error(
          pc.red(
            `Error: ERC721OwnerPredicate is not deployed on ${options.network}.`,
          ),
        )
        console.error(
          pc.yellow(
            "  Use --access-predicate to specify the predicate address manually.",
          ),
        )
        process.exit(1)
      }
      accessPredicate = predicateAddr
      nftGateInfo = { predicateAddr }
    } else if (options.erc20Gate && erc20MinBalance !== undefined) {
      const predicateAddr = deploymentAddress(ERC20_BALANCE_PREDICATE, chain.id)
      if (!predicateAddr) {
        console.error(
          pc.red(
            `Error: ERC20BalancePredicate is not deployed on ${options.network}.`,
          ),
        )
        console.error(
          pc.yellow(
            "  Use --access-predicate to specify the predicate address manually.",
          ),
        )
        process.exit(1)
      }
      accessPredicate = predicateAddr
      erc20GateInfo = {
        predicateAddr,
        token: options.erc20Gate as `0x${string}`,
        minBalance: erc20MinBalance,
      }
    }

    const address = (await wallet.getAddress()).toLowerCase()

    if (manifest.creatorAddress !== address) {
      console.error(
        pc.red(
          `Error: manifest.creatorAddress (${manifest.creatorAddress}) does not match your wallet (${address}). The ERC-8257 spec requires these to match.`,
        ),
      )
      process.exit(1)
    }

    console.log(pc.cyan("\nRegistration summary:"))
    console.log(`  Tool: ${manifest.name}`)
    console.log(`  Network: ${options.network}`)
    console.log(`  Wallet: ${address} (${wallet.name})`)
    console.log(`  Metadata URI: ${options.metadata}`)
    console.log(`  Manifest Hash: ${hash}`)
    if (nftGateInfo) {
      console.log(
        `  Access Predicate: ${nftGateInfo.predicateAddr} (ERC721OwnerPredicate, gating collection ${options.nftGate})`,
      )
    } else if (erc20GateInfo) {
      console.log(
        `  Access Predicate: ${erc20GateInfo.predicateAddr} (ERC20BalancePredicate, token ${erc20GateInfo.token}, minBalance ${erc20GateInfo.minBalance})`,
      )
    } else if (options.accessPredicate) {
      const label = predicateName
        ? `${accessPredicate} (${predicateName})`
        : `${accessPredicate}`
      console.log(`  Access Predicate: ${label}`)
      if (predicateConfig) {
        console.log(`  Predicate Config: ${JSON.stringify(predicateConfig)}`)
      }
    } else {
      console.log(`  Access Predicate: ${accessPredicate}`)
    }

    if (!manifest.access && options.nftGate) {
      const predicate = new ERC721OwnerPredicateClient({
        chain,
        predicateAddress: nftGateInfo?.predicateAddr,
      })
      const access = predicate.toManifestAccess(
        options.nftGate as `0x${string}`,
      )
      const accessJson = JSON.stringify(access, null, 2)

      console.log(
        pc.yellow(
          "\nYour manifest does not include an access field. " +
            "Adding one lets agents discover the gating requirement and find your collection on OpenSea.",
        ),
      )
      console.log(pc.cyan("\nSuggested access block:"))
      console.log(accessJson)
    }

    if (!manifest.access && erc20GateInfo) {
      const predicate = new ERC20BalancePredicateClient({
        chain,
        predicateAddress: erc20GateInfo.predicateAddr,
      })
      const access = predicate.toManifestAccess(
        erc20GateInfo.token,
        erc20GateInfo.minBalance,
      )
      const accessJson = JSON.stringify(access, null, 2)

      console.log(
        pc.yellow(
          "\nYour manifest does not include an access field. " +
            "Adding one lets agents discover the gating requirement.",
        ),
      )
      console.log(pc.cyan("\nSuggested access block:"))
      console.log(accessJson)
    }

    if (options.accessPredicate && !predicateConfig) {
      const configHint =
        predicateName === "ERC721OwnerPredicate"
          ? 'Use --predicate-config \'{"collections":["0x..."]}\' or run `tool-sdk set-collections` after registration.'
          : predicateName === "ERC1155OwnerPredicate"
            ? 'Use --predicate-config \'{"collection":"0x...","tokenIds":["1","2"]}\' or run `tool-sdk set-collection-tokens` after registration.'
            : predicateName === "SubscriptionPredicate"
              ? 'Use --predicate-config \'{"collection":"0x...","minTier":0}\' or run `tool-sdk configure-subscription` after registration.'
              : predicateName === "ERC20BalancePredicate"
                ? 'Use --predicate-config \'{"token":"0x...","minBalance":"1000000000000000000"}\' or run `tool-sdk configure-erc20-gate` after registration.'
                : "Configure the predicate after registration to enforce access control."
      console.log(
        pc.yellow(
          `\n  WARNING: predicate ${accessPredicate} registered but not configured.` +
            "\n  Tool will accept any caller until you configure the predicate." +
            `\n  ${configHint}`,
        ),
      )
    }

    if (options.dryRun) {
      if (predicateConfig && options.accessPredicate) {
        console.log(
          pc.cyan("\n  Predicate config TX would be sent after registration."),
        )
      }
      console.log(pc.yellow("\n  --dry-run: no transaction sent"))
      return
    }

    if (!options.yes) {
      const clack = await import("@clack/prompts")
      const confirm = await clack.confirm({
        message: "Proceed with registration?",
      })
      if (!confirm || clack.isCancel(confirm)) {
        console.log(pc.yellow("Cancelled"))
        return
      }
    }

    const walletClient = await walletAdapterToClient(wallet, chain, rpcUrl)

    const registry = new ToolRegistryClient({
      chain,
      rpcUrl,
      walletClient,
    })

    let regResult: { toolId: bigint; txHash: string }
    try {
      regResult = await registry.registerTool({
        metadataURI: options.metadata,
        // Hash the served document as-is (ERC-8257 §2), not the stripped copy.
        manifest: data as object,
        accessPredicate,
      })
    } catch (err) {
      console.error(pc.red("Error registering tool:"))
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
      return
    }

    console.log(pc.green("\nTool registered!"))
    console.log(`  Tool ID: ${regResult.toolId}`)
    console.log(`  TX Hash: ${regResult.txHash}`)

    if (options.nftGate && nftGateInfo) {
      console.log(
        pc.cyan(
          `\nNext step: configure the predicate to gate on your collection:\n` +
            `  tool-sdk set-collections ${regResult.toolId} ${options.nftGate} --network ${options.network}`,
        ),
      )
    }

    if (erc20GateInfo) {
      try {
        const predicate = new ERC20BalancePredicateClient({
          chain,
          walletClient,
          predicateAddress: erc20GateInfo.predicateAddr,
        })
        const txHash = await predicate.configureToolERC20(
          regResult.toolId,
          erc20GateInfo.token,
          erc20GateInfo.minBalance,
        )
        const publicClient = createPublicClient({
          chain,
          transport: http(rpcUrl),
        })
        await publicClient.waitForTransactionReceipt({ hash: txHash })
        console.log(pc.green("\nERC-20 balance gate configured!"))
        console.log(`  configureToolERC20 TX: ${txHash}`)
        console.log(`  Token: ${erc20GateInfo.token}`)
        console.log(`  Min Balance: ${erc20GateInfo.minBalance}`)
      } catch (err) {
        console.error(
          pc.red(
            `\nTool registered as toolId ${regResult.toolId}, but ERC-20 gate config failed:`,
          ),
        )
        console.error(err instanceof Error ? err.message : String(err))
        console.error(
          pc.yellow(
            `\n  Recovery: run \`tool-sdk configure-erc20-gate ${regResult.toolId} ${erc20GateInfo.token} ${erc20GateInfo.minBalance} --network ${options.network}\``,
          ),
        )
        process.exit(1)
      }
    }

    if (
      options.accessPredicate &&
      predicateName === "SubscriptionPredicate" &&
      !predicateConfig
    ) {
      console.log(
        pc.cyan(
          `\nNext step: configure the subscription gate:\n` +
            `  tool-sdk configure-subscription ${regResult.toolId} <collection> --network ${options.network}`,
        ),
      )
    }

    if (options.accessPredicate && predicateConfig) {
      try {
        await executePredicateConfig({
          predicateName,
          predicateAddress: accessPredicate,
          toolId: regResult.toolId,
          config: predicateConfig,
          chain,
          walletClient,
          rpcUrl,
        })
      } catch (err) {
        console.error(
          pc.red(
            `\nTool registered as toolId ${regResult.toolId}, but predicate config failed:`,
          ),
        )
        console.error(err instanceof Error ? err.message : String(err))
        let recoveryCmd: string
        if (predicateName === "ERC1155OwnerPredicate") {
          recoveryCmd = `tool-sdk set-collection-tokens ${regResult.toolId} <collection> <tokenIds...> --network ${options.network}`
        } else if (predicateName === "SubscriptionPredicate") {
          recoveryCmd = `tool-sdk configure-subscription ${regResult.toolId} <collection> --network ${options.network}`
        } else if (predicateName === "ERC20BalancePredicate") {
          recoveryCmd = `tool-sdk configure-erc20-gate ${regResult.toolId} <token> <minBalance> --network ${options.network}`
        } else {
          recoveryCmd = `tool-sdk set-collections ${regResult.toolId} <collection> --network ${options.network}`
        }
        console.error(pc.yellow(`\n  Recovery: run \`${recoveryCmd}\``))
        process.exit(1)
      }
    }
  })

interface PredicateConfigParams {
  predicateName: string | undefined
  predicateAddress: `0x${string}`
  toolId: bigint
  config: Record<string, unknown>
  chain: Chain
  walletClient: WalletClient<Transport, Chain, Account>
  rpcUrl?: string
}

function validatePredicateConfig(
  predicateName: string,
  config: Record<string, unknown>,
) {
  if (predicateName === "ERC721OwnerPredicate") {
    const collections = config.collections
    if (!Array.isArray(collections) || collections.length === 0) {
      console.error(
        pc.red(
          'Error: ERC721OwnerPredicate config requires "collections" array of addresses',
        ),
      )
      process.exit(1)
    }
    for (const c of collections) {
      if (typeof c !== "string" || !isAddress(c)) {
        console.error(pc.red(`Error: invalid collection address "${c}"`))
        process.exit(1)
      }
    }
  } else if (predicateName === "ERC1155OwnerPredicate") {
    const collection = config.collection
    const tokenIds = config.tokenIds
    if (typeof collection !== "string" || !isAddress(collection)) {
      console.error(
        pc.red(
          'Error: ERC1155OwnerPredicate config requires "collection" address',
        ),
      )
      process.exit(1)
    }
    if (!Array.isArray(tokenIds) || tokenIds.length === 0) {
      console.error(
        pc.red('Error: ERC1155OwnerPredicate config requires "tokenIds" array'),
      )
      process.exit(1)
    }
    try {
      tokenIds.map(t => BigInt(t as string))
    } catch {
      console.error(pc.red("Error: invalid token ID (must be numeric)"))
      process.exit(1)
    }
  } else if (predicateName === "SubscriptionPredicate") {
    const collection = config.collection
    if (typeof collection !== "string" || !isAddress(collection)) {
      console.error(
        pc.red(
          'Error: SubscriptionPredicate config requires "collection" address',
        ),
      )
      process.exit(1)
    }
    if (config.minTier !== undefined) {
      const tier = Number(config.minTier)
      if (!Number.isInteger(tier) || tier < 0 || tier > 255) {
        console.error(
          pc.red("Error: minTier must be an integer between 0 and 255"),
        )
        process.exit(1)
      }
    }
  } else if (predicateName === "ERC20BalancePredicate") {
    const token = config.token
    if (typeof token !== "string" || !isAddress(token)) {
      console.error(
        pc.red('Error: ERC20BalancePredicate config requires "token" address'),
      )
      process.exit(1)
    }
    const minBalance = config.minBalance
    if (minBalance === undefined) {
      console.error(
        pc.red('Error: ERC20BalancePredicate config requires "minBalance"'),
      )
      process.exit(1)
    }
    try {
      const val = BigInt(minBalance as string)
      if (val <= 0n) throw new Error("must be positive")
    } catch {
      console.error(pc.red("Error: minBalance must be a positive integer"))
      process.exit(1)
    }
  }
}

async function executePredicateConfig(params: PredicateConfigParams) {
  const {
    predicateName,
    predicateAddress,
    toolId,
    config,
    chain,
    walletClient,
  } = params

  if (predicateName === "ERC721OwnerPredicate") {
    const collections = (config.collections as string[]).map(c => c as Address)
    const predicate = new ERC721OwnerPredicateClient({
      chain,
      walletClient,
      predicateAddress,
    })
    const txHash = await predicate.setCollections(toolId, collections)
    console.log(pc.green("\nPredicate configured!"))
    console.log(`  setCollections TX: ${txHash}`)
    return
  }

  if (predicateName === "ERC1155OwnerPredicate") {
    const collection = config.collection as Address
    const parsedTokenIds = (config.tokenIds as string[]).map(t => BigInt(t))
    const predicate = new ERC1155OwnerPredicateClient({
      chain,
      walletClient,
      predicateAddress,
    })
    const txHash = await predicate.setCollectionTokens(toolId, [
      { collection, tokenIds: parsedTokenIds },
    ])
    console.log(pc.green("\nPredicate configured!"))
    console.log(`  setCollectionTokens TX: ${txHash}`)
    return
  }

  if (predicateName === "SubscriptionPredicate") {
    const collection = config.collection as Address
    const minTier = config.minTier !== undefined ? Number(config.minTier) : 0
    const predicateAddr = deploymentAddress(SUBSCRIPTION_PREDICATE, chain.id)
    const predicate = new SubscriptionPredicateClient({
      chain,
      walletClient,
      predicateAddress: predicateAddr ?? predicateAddress,
      rpcUrl: params.rpcUrl,
    })
    const txHash = await predicate.configureToolGating(
      toolId,
      collection,
      minTier,
    )
    const publicClient = createPublicClient({
      chain,
      transport: http(params.rpcUrl),
    })
    await publicClient.waitForTransactionReceipt({ hash: txHash })
    console.log(pc.green("\nSubscription predicate configured!"))
    console.log(`  configureToolGating TX: ${txHash}`)
    console.log(`  Collection: ${collection}`)
    console.log(`  Min Tier: ${minTier}`)
    return
  }

  if (predicateName === "ERC20BalancePredicate") {
    const token = config.token as Address
    const minBalance = BigInt(config.minBalance as string)
    const predicateAddr = deploymentAddress(ERC20_BALANCE_PREDICATE, chain.id)
    const predicate = new ERC20BalancePredicateClient({
      chain,
      walletClient,
      predicateAddress: predicateAddr ?? predicateAddress,
      rpcUrl: params.rpcUrl,
    })
    const txHash = await predicate.configureToolERC20(toolId, token, minBalance)
    const publicClient = createPublicClient({
      chain,
      transport: http(params.rpcUrl),
    })
    await publicClient.waitForTransactionReceipt({ hash: txHash })
    console.log(pc.green("\nERC-20 balance predicate configured!"))
    console.log(`  configureToolERC20 TX: ${txHash}`)
    console.log(`  Token: ${token}`)
    console.log(`  Min Balance: ${minBalance}`)
    return
  }

  console.log(
    pc.yellow(
      `\n  WARNING: predicate "${predicateName ?? "unknown"}" is not a recognized type.` +
        "\n  --predicate-config was ignored. Configure the predicate manually.",
    ),
  )
}
